import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortal } from '../../context/PortalContext';
import config from '../../config';
import axios from 'axios';
import SectionHeader from '../../components/SectionHeader/SectionHeader';
import './DeveloperPortalLayout.css';

interface OAuthClient {
  id: string;
  client_id: string;
  client_secret: string;
  name: string;
  description: string;
  redirect_uris: string[];
  allowed_domains: string[];
  scopes: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ApplicationsProps {
  onNavigate: (section: string) => void;
}

const Applications: React.FC<ApplicationsProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const { createOAuthClient } = usePortal();
  const [clients, setClients] = useState<OAuthClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState<{ [key: string]: boolean }>({});
  const [copiedItems, setCopiedItems] = useState<{ [key: string]: boolean }>({});
  const [editingClient, setEditingClient] = useState<OAuthClient | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [allowedDomainsInput, setAllowedDomainsInput] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    redirect_uris: [] as string[],
    allowed_domains: [] as string[],
    scopes: [] as string[]
  });

  // Load user's OAuth clients
  const loadClients = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/oauth/clients`);
      setClients(response.data);
      setError(null);
    } catch (err) {
      setError('Error loading OAuth clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadClients();
    }
  }, [user]);

  const toggleSecretVisibility = (clientId: string) => {
    setShowSecret(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  const copyToClipboard = (text: string, itemKey: string) => {
    navigator.clipboard.writeText(text);
    
    // Show "Copied!" feedback
    setCopiedItems(prev => ({
      ...prev,
      [itemKey]: true
    }));
    
    // Hide "Copied!" after 2 seconds
    setTimeout(() => {
      setCopiedItems(prev => ({
        ...prev,
        [itemKey]: false
      }));
    }, 2000);
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setAllowedDomainsInput('');
    setEditForm({
      name: '',
      description: '',
      redirect_uris: [],
      allowed_domains: [],
      scopes: []
    });
  };

  const handleEditClient = (client: OAuthClient) => {
    setIsCreating(false);
    setEditingClient(client);
    setAllowedDomainsInput(client.allowed_domains.join(', '));
    setEditForm({
      name: client.name,
      description: client.description,
      redirect_uris: [...client.redirect_uris],
      allowed_domains: [...client.allowed_domains],
      scopes: [...client.scopes]
    });
  };

  const handleSaveEdit = async () => {
    // Scopes are optional - use generic scope for all clients
    const allScopes = ['general:access'];
    
    // Parse allowed domains from the input string
    const parsedAllowedDomains = allowedDomainsInput
      .split(',')
      .map(domain => domain.trim())
      .filter(domain => domain.length > 0);
    
    if (isCreating) {
      // Create new application
      try {
        const clientData = {
          name: editForm.name,
          description: editForm.description,
          redirect_uris: editForm.redirect_uris,
          allowed_domains: parsedAllowedDomains,
          scopes: allScopes
        };
        
        const result = await createOAuthClient(clientData);
        
        if (result) {
          // Close the modal
          setIsCreating(false);
          setAllowedDomainsInput('');
          setEditForm({
            name: '',
            description: '',
            redirect_uris: [],
            allowed_domains: [],
            scopes: []
          });
          
          // Reload clients to get updated data
          loadClients();
        } else {
          setError('Error creating application');
        }
      } catch (err) {
        setError('Error creating application');
      }
      return;
    }

    if (!editingClient) return;
    
    try {
      const formData = new FormData();
      formData.append('name', editForm.name);
      formData.append('description', editForm.description);
      formData.append('redirect_uris', JSON.stringify(editForm.redirect_uris));
      formData.append('allowed_domains', JSON.stringify(parsedAllowedDomains));
      formData.append('scopes', JSON.stringify(allScopes));
      
      await axios.put(`${config.API_BASE_URL}/oauth/portal/update/${editingClient.client_id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Close the modal
      setEditingClient(null);
      setAllowedDomainsInput('');
      setEditForm({
        name: '',
        description: '',
        redirect_uris: [],
        allowed_domains: [],
        scopes: []
      });
      
      // Reload clients to get updated data
      loadClients();
    } catch (err) {
      setError('Error updating application');
    }
  };

  const handleCancelEdit = () => {
    setEditingClient(null);
    setIsCreating(false);
    setAllowedDomainsInput('');
    setEditForm({
      name: '',
      description: '',
      redirect_uris: [],
      allowed_domains: [],
      scopes: []
    });
  };

  return (
    <div className="applications">
      <SectionHeader
        title="My Applications"
        description="Manage your OAuth applications and view credentials"
        rightContent={
          clients.length > 0 ? (
            <button 
              className="btn-primary header-action"
              onClick={handleCreateNew}
            >
              Register New App
            </button>
          ) : null
        }
      />
      
      {loading ? (
        <div className="loading">Loading applications...</div>
      ) : (
        <>
          {error && <div className="error-message">{error}</div>}
          
          {clients.length === 0 ? (
        <div className="empty-state">
          <div className="empty-message">
            <h3>No applications yet</h3>
            <p>Register your first OAuth application to get started with PlatefullAI integration.</p>
            <button onClick={handleCreateNew} className="btn-primary">
              Register New App
            </button>
          </div>
        </div>
      ) : (
        <div className="applications-grid">
          {clients.map((client) => (
            <div key={client.id} className="application-card">
              <div className="app-header">
                <div className="app-title">
                  <h3>{client.name}</h3>
                  <span className={`status ${client.is_active ? 'active' : 'inactive'}`}>
                    {client.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="app-actions">
                  <button 
                    className="btn-secondary"
                    onClick={() => handleEditClient(client)}
                  >
                    Edit
                  </button>
                </div>
              </div>
              
              {client.description && (
                <p className="app-description">{client.description}</p>
              )}
              
              <div className="credentials-section">
                <h4>Credentials</h4>
                
                <div className="credential-item">
                  <label>Client ID</label>
                  <div className="credential-value">
                    <code>{client.client_id}</code>
                    <button 
                      onClick={() => copyToClipboard(client.client_id, `client-id-${client.id}`)}
                      className="btn-copy"
                      title="Copy Client ID"
                    >
                      <span className={`copy-text ${copiedItems[`client-id-${client.id}`] ? 'copied' : ''}`}>
                        {copiedItems[`client-id-${client.id}`] ? 'Copied!' : 'Copy'}
                      </span>
                    </button>
                  </div>
                </div>
                
                <div className="credential-item">
                  <label>Client Secret</label>
                  <div className="credential-value">
                    <code className={showSecret[client.id] ? 'secret-visible' : 'secret-hidden'}>
                      {showSecret[client.id] ? client.client_secret : '••••••••••••••••'}
                    </code>
                    <button 
                      onClick={() => toggleSecretVisibility(client.id)}
                      className="btn-toggle"
                      title={showSecret[client.id] ? 'Hide Secret' : 'Show Secret'}
                    >
                      {showSecret[client.id] ? '👁' : '👁'}
                    </button>
                    <button 
                      onClick={() => copyToClipboard(client.client_secret, `client-secret-${client.id}`)}
                      className="btn-copy"
                      title="Copy Client Secret"
                    >
                      <span className={`copy-text ${copiedItems[`client-secret-${client.id}`] ? 'copied' : ''}`}>
                        {copiedItems[`client-secret-${client.id}`] ? 'Copied!' : 'Copy'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="app-details">
                <div className="detail-section">
                  <h4>Redirect URIs</h4>
                  <div className="uri-list">
                    {client.redirect_uris.map((uri, index) => (
                      <code key={index} className="uri-item">{uri}</code>
                    ))}
                  </div>
                </div>
                
                <div className="detail-section">
                  <h4>Allowed Domains</h4>
                  <div className="domain-list">
                    {client.allowed_domains.map((domain, index) => (
                      <span key={index} className="domain-tag">{domain}</span>
                    ))}
                  </div>
                </div>
                
                {/* Scopes removed - API provides general access to all clients */}
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}

      {/* Create/Edit Modal */}
      {(isCreating || editingClient) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{isCreating ? 'Register New Application' : 'Edit Application'}</h2>
              <button className="modal-close" onClick={handleCancelEdit}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="edit-name">Application Name</label>
                <input
                  id="edit-name"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="form-input"
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-redirect-uris">Redirect URIs (one per line)</label>
                <textarea
                  id="edit-redirect-uris"
                  value={editForm.redirect_uris.join('\n')}
                  onChange={(e) => setEditForm(prev => ({ 
                    ...prev, 
                    redirect_uris: e.target.value.split('\n').filter(uri => uri.trim()) 
                  }))}
                  className="form-input"
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-allowed-domains">Allowed Domains (comma-separated)</label>
                <input
                  id="edit-allowed-domains"
                  type="text"
                  value={allowedDomainsInput}
                  onChange={(e) => setAllowedDomainsInput(e.target.value)}
                  className="form-input"
                  placeholder="myapp.com, www.myapp.com, localhost:3000"
                />
              </div>
              
              {/* API Permissions removed - All applications get full access by default */}
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveEdit}>
                {isCreating ? 'Create Application' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;
